import { useCourtFilingMarks } from "components/CourtFilingMarks";
import { useTooltip } from "context/TooltipProvider";
import { FC, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { editSuspectToWitness2 } from "services/caseServices";
import { SuspectTowitness } from "types";
import { errorHandlerOnForm } from "utils/helper";

type Props = {
  conversion: SuspectTowitness;
  onClose: (refetch: boolean) => void;
};

const RegistrarAction: FC<Props> = ({ conversion, onClose }) => {
  const { t } = useTranslation();
  // const navigate = useNavigate();
  const { showTooltip } = useTooltip();
  // const [loading, setLoading] = useState(false);
  const [formState, setFormState] = useState<SuspectTowitness>(conversion);
  // Office stamp and signature printed on the agreement filed to court.
  const courtMarks = useCourtFilingMarks(true);

  useEffect(() => {
    if (conversion) {
      setFormState(conversion);
    }
  }, [conversion]);

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >,
  ) => {
    const { name, value } = e.target;
    setFormState((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  const validateForm = () => {
    if (!formState.registration_date) return t("registration_date_required");
    else if (!formState.registration_number)
      return t("registration_number_required");

    return "";
  };

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();

    const error = validateForm();
    if (error) {
      showTooltip(error, "error");
      return;
    }

    // setLoading(true);
    // Submit the FormData
    const registrationDate = formState.registration_date as unknown;
    const withMarks = !!(
      courtMarks.marks.stamp_file || courtMarks.marks.signature_file
    );
    editSuspectToWitness2(
      // With a stamp or signature the request is multipart, so only the
      // fields filled here are sent (the record has nested data).
      withMarks
        ? {
            id: formState.id,
            registration_number: formState.registration_number,
            registration_date:
              registrationDate instanceof Date
                ? registrationDate.toISOString().substring(0, 10)
                : registrationDate,
            status: "sent_to_court",
          }
        : { ...formState, status: "sent_to_court" },
      courtMarks.marks,
    )
      .then(() => {
        showTooltip(t("successfully_submitted"), "success");
        onClose(true);
      })
      .catch((error) => {
        showTooltip(
          error?.response?.data?.detail || errorHandlerOnForm(error),
          "error",
        );
      });
    // .finally(() => setLoading(false));
  };
  const handleClickOutside = (e: any) => {
    if (e.target.classList.contains("fixed")) {
      onClose(false);
    }
  };

  return (
    <div
      style={{ marginTop: "0px" }}
      className="fixed inset-0 z-50 flex justify-end bg-black bg-opacity-50 backdrop-blur-sm p-4"
      onClick={handleClickOutside}
    >
      <div className="relative w-full max-w-2xl bg-white p-8 shadow-lg rounded-lg overflow-y-auto">
        <h2 className="mb-1 text-2xl font-semibold">
          {t("send_to_court")}: {t("suspect_to_witness")}
        </h2>
        <p className="mb-6 text-xs font-light text-[#9D9D9D]">
          {t("stw_send_to_court_description")}
        </p>

        <form onSubmit={handleSubmit}>
          <div>
            <div className="">
              <div className="flex flex-col mt-4">
                <label className="text-sm text-[#747171] font-[inter] font-[400] mb-2">
                  {t("registration_date")}
                </label>
                <input
                  type="date"
                  className="bg-[#F2F2F2] rounded-lg p-2 focus:outline-none focus:ring focus:ring-blue-300"
                  name="registration_date"
                  value={
                    formState?.registration_date
                      ? typeof formState.registration_date === "string"
                        ? formState.registration_date.substring(0, 10)
                        : ""
                      : ""
                  }
                  onChange={handleChange}
                />
              </div>

              <div className="flex flex-col mt-4">
                <label className="text-sm text-[#747171] font-[inter] font-[400] mb-2">
                  {t("registration_number")}
                </label>
                <input
                  type="text"
                  className="bg-[#F2F2F2] rounded-lg p-2 focus:outline-none focus:ring focus:ring-blue-300"
                  name="registration_number"
                  value={formState?.registration_number ?? ""}
                  onChange={(e) => {
                    setFormState({
                      ...formState,
                      registration_number: e.target.value,
                    });
                  }}
                  // disabled
                />
              </div>
            </div>
          </div>

          <div className="mt-4">{courtMarks.fields}</div>

          <div className="flex flex-row mt-4 gap-3">
            <input
              type="submit"
              className="border border-[#212121] bg-[#212121] px-[76px] py-3 text-white font-[Poppins] cursor-pointer font-[500] text-base rounded-md hover:bg-[#414040]"
              value={t("send")}
            />
          </div>
        </form>
      </div>
    </div>
  );
};

export default RegistrarAction;
